import React from 'react';
import { cn } from '@/lib/utils';
import { getImageAssetPath } from '@/lib/imageHelper';
import OptimizedImage from '@/components/common/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { getCarouselLogos } from '@/config/references';

export interface LogoCarouselProps {
  /**
   * Zusätzliche CSS-Klassen
   */
  className?: string;
  
  /**
   * Geschwindigkeit der Animation in Sekunden
   * @default 80
   */
  speed?: number;
  
  /**
   * Titel über dem Carousel (optional)
   */
  title?: string;
  
  /**
   * Beschreibung über dem Carousel (optional)
   */
  description?: string;
  
  /**
   * Höhe der Logos in Pixeln
   * @default 120
   */
  logoHeight?: number;

  /**
   * Maximale Breite der Logos in Pixeln
   * @default 200
   */
  logoMaxWidth?: number;

  /**
   * Button-Text unter dem Carousel (optional)
   */
  buttonText?: string;

  /**
   * Button-Link unter dem Carousel (optional)
   * @default '/references'
   */
  buttonLink?: string;
}

/**
 * Ein Carousel zur Anzeige von Kundenlogos, das automatisch scrollt
 */
export const LogoCarousel = ({
  className,
  speed = 80,
  title,
  description,
  logoHeight = 120,
  logoMaxWidth = 200,
  buttonText,
  buttonLink = '/references'
}: LogoCarouselProps) => {
  const { t } = useTranslation();
  
  // Hole die Logos aus der zentralen Konfigurationsdatei
  const logoFiles = getCarouselLogos();
  
  return (
    <div className={cn('py-10 overflow-hidden', className)}>
      {(title || description) && (
        <div className="container mb-8 text-center">
          {title && <h2 className="text-2xl font-bold mb-2">{title}</h2>}
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
      )}
      
      <div className="relative w-full">
        {/* Gradient-Effekt links */}
        <div className="absolute left-0 top-0 h-full w-[100px] bg-gradient-to-r from-white to-transparent z-10" />
        
        {/* Scrollender Inhalt */}
        <div 
          className="flex items-center gap-12 animate-scroll"
          style={{ 
            '--scroll-speed': `${speed}s`,
            animationDuration: `${speed}s`
          } as React.CSSProperties}
        >
          {/* Erste Gruppe von Logos */}
          {logoFiles.map((logo, index) => (
            <div 
              key={`logo-1-${index}`} 
              className="flex-shrink-0 flex items-center justify-center"
              style={{ 
                width: logoMaxWidth, 
                height: logoHeight,
                minWidth: logoMaxWidth 
              }}
            >
              <OptimizedImage
                src={getImageAssetPath(`references/logo/${logo}`)}
                alt={`Referenzlogo ${logo.split('.')[0]}`}
                width={logoMaxWidth}
                height={logoHeight}
                style={{ 
                  maxHeight: logoHeight, 
                  maxWidth: logoMaxWidth,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                loading="lazy"
              />
            </div>
          ))}
          
          {/* Duplizierte Gruppe für nahtloses Scrollen */}
          {logoFiles.map((logo, index) => (
            <div 
              key={`logo-2-${index}`} 
              className="flex-shrink-0 flex items-center justify-center"
              style={{ 
                width: logoMaxWidth, 
                height: logoHeight,
                minWidth: logoMaxWidth 
              }}
            >
              <OptimizedImage
                src={getImageAssetPath(`references/logo/${logo}`)}
                alt={`Referenzlogo ${logo.split('.')[0]}`}
                width={logoMaxWidth}
                height={logoHeight}
                style={{ 
                  maxHeight: logoHeight, 
                  maxWidth: logoMaxWidth,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
        
        {/* Gradient-Effekt rechts */}
        <div className="absolute right-0 top-0 h-full w-[100px] bg-gradient-to-l from-white to-transparent z-10" />
      </div>

      {/* Button zum Anzeigen der Projekte */}
      {buttonText && (
        <div className="container mt-8 text-center">
          <Button asChild className="px-6">
            <Link to={buttonLink}>{buttonText}</Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default LogoCarousel; 